import React, { useState } from 'react';
import { Flex, Spinner, Text } from '@zillow/constellation';
import { useDispatch, useSelector } from 'react-redux';

import AutocompleteAreaInput from 'app/shared/components/AutocompleteAreaInput';
import DataCard from 'app/shared/components/DataCard';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';
import { handleCompareMarketChange } from 'app/shared/flux/actions/FilterActions';

import constants from 'app/shared/flux/constants';
import * as S from './styles';
import { augmentError, formatForLog } from 'app/shared/utils/errors';
import filterUtils from 'app/shared/utils/filterUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import { trackEvent } from 'app/shared/utils/analytics/trackEvent';
import { gaEvents } from 'app/shared/constants/AnalyticsConstants';

import type AutocompleteArea from 'app/shared/models/AutocompleteArea';
import type { Market } from 'app/shared/types/Market';
import type { State } from 'app/shared/types/State';

interface CompareRentProps {
  market: Market;
  isSmallScreen: boolean;
  isMobile: boolean;
}

const logger = getLogger('components/CompareRent');

const CompareRent: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<CompareRentProps>>> = (
  props,
) => {
  const { market, isSmallScreen, isMobile } = props;
  const dispatch = useDispatch();
  const {
    filter,
    marketTrends: {
      medianNationalRent,
      rentCompare: { medianRent: otherMedianRent, areaName },
    },
  } = useSelector((state: State) => state);

  const displayAreaName = areaName === 'US' || !areaName ? 'the national median' : areaName;

  const medianRent = otherMedianRent || medianNationalRent || 0;
  const { bedrooms } = useMarketTrendsFilters(filter, dispatch);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rentMultiplier = market.medianRent / medianRent - 1;
  const percentDifference = Math.abs(rentMultiplier * 100);
  const displayPercentage =
    percentDifference < 1
      ? numberUtils.asPercent(percentDifference, '0.00%')
      : numberUtils.asPercent(percentDifference);

  const submit = (area: AutocompleteArea) => {
    async function updateOtherMarket() {
      if (area.areaId) {
        setLoading(true);
        setError(null);
        const resourceId = market.resourceId;
        const otherResourceId = area.resourceId || '';
        // @ts-ignore
        const { data, errors } = await dispatch(
          handleCompareMarketChange(
            {
              bedrooms: filterUtils.bedroomsEnumToFilter(bedrooms),
              propertyTypes: filter.propertyTypes,
            },
            otherResourceId,
            resourceId,
          ),
        );

        if (errors.length > 0) {
          setLoading(false);
          setError('An error occurred. Please try again.');
          throw new Error(errors[0].code);
        }

        dispatch({
          type: constants.SET_MARKET_STATS,
          payload: {
            rentCompare: {
              medianRent: data?.marketPage?.rentCompare?.medianRent ?? 0,
              otherResourceId: otherResourceId,
              areaName: area.longName,
            },
          },
        });

        setLoading(false);

        if (medianRent !== 0) {
          trackEvent({ ...gaEvents.COMPARE_TO_MARKET, label: otherResourceId });
        } else {
          trackEvent({
            ...gaEvents.COMPARE_TO_MARKET_NO_DATA,
            label: area.resourceId || 'EMPTY',
          });
        }
      }
    }

    try {
      updateOtherMarket();
    } catch (err) {
      augmentError(err, {
        root: 'components/CompareRent#submit',
      });
      logger.error(formatForLog(err), `Compare Rent: error fetching median rent for ${area.longName}`);
    }
  };

  return (
    <DataCard
      heading="Compare Median Rent in Markets"
      copyText={
        medianRent === 0 ? (
          `Data is not available for ${displayAreaName}.`
        ) : medianRent === market.medianRent ? (
          `Rent in ${market.name} is equal to rent in ${displayAreaName}.`
        ) : (
          <>
            Rent in {market.name} is <strong>{displayPercentage}</strong> {rentMultiplier >= 0 ? 'higher' : 'lower'}{' '}
            than {displayAreaName}.
          </>
        )
      }
      useFullScreen={isSmallScreen}
    >
      <S.Stripe display="flex" justifyContent="center" flexDirection="row" marginTop="sm">
        <S.MarketName fontType={isMobile ? 'bodySmallHeading' : 'bodyHeading'}>{market.name}</S.MarketName>
        <S.Versus fontType={isMobile ? 'bodySmallHeading' : 'bodyHeading'}>vs.</S.Versus>
        <S.AutocompleteContainer padding="xs">
          <AutocompleteAreaInput
            submit={submit}
            type="secondary"
            error={error}
            blockedAreas={[market.resourceId]}
            placeholder="Search"
          />
        </S.AutocompleteContainer>
      </S.Stripe>
      <Flex display="flex" alignItems="center" justifyContent="space-evenly" flexDirection="row" flex="1">
        <S.RentPrice fontType="h2" level="2" fontFamily="serif" fontColor="blue500">
          {numberUtils.asCurrency(market.medianRent)}
        </S.RentPrice>
        <S.OtherRentPrice fontType="h2" level="2" fontFamily="serif" fontColor="aqua400">
          {loading ? (
            <Spinner />
          ) : medianRent !== 0 ? (
            numberUtils.asCurrency(medianRent)
          ) : (
            <>
              --
              <Text as="p" fontType="bodySmall" fontColor="coolGray500" paddingX="md">
                Data is not available. Please try a different market.
              </Text>
            </>
          )}
        </S.OtherRentPrice>
      </Flex>
    </DataCard>
  );
};

export default CompareRent;
